import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardMedia, Typography, Box, Button } from '@mui/material'
import { makeStyles } from '@mui/styles'
import MtLogo from '../logos/mindtools_logo_primary_rgb.svg'
import { getContentImageUrl, THUMBNAIL_SIZES } from '../../helpers/content-image'

const useStyles = makeStyles(({ spacing, breakpoints, customShadows, radius }) => ({
  mobileContentCard: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: radius['2xl'],
    boxShadow: customShadows.card,
    border: 'none',
    height: '360px'
  },
  mobileContentImage: {
    objectFit: 'cover',
    height: '180px',
    [breakpoints.down('md')]: {
      height: '160px'
    },
    [breakpoints.down('sm')]: {
      height: '140px'
    }
  },
  mobileContentCardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
    padding: spacing(2),
    flex: 1,
    justifyContent: 'space-between',
    overflow: 'hidden'
  },
  mobileContentTitle: {
    fontWeight: 800,
    color: '#000000',
    textAlign: 'left',
    lineHeight: 1.3,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    minHeight: '2.6em',
    maxHeight: '3.9em',
    [breakpoints.up('md')]: {
      minHeight: 'auto',
      maxHeight: 'none'
    }
  },
  mobileContentDescription: {
    fontWeight: 400,
    color: '#000000',
    textAlign: 'left',
    lineHeight: 1.4,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    marginBottom: spacing(0.5),
    minHeight: '2.8em',
    maxHeight: '4.2em',
    [breakpoints.down('md')]: {
      WebkitLineClamp: 2,
      minHeight: 'auto',
      maxHeight: 'none'
    }
  },
  mobileContentActions: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 'auto'
  },
  mobileReadMoreButton: {
    borderRadius: radius.md,
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: '#5D5BED',
    color: '#FFFFFF',
    padding: `${spacing(0.75)} ${spacing(2)}`,
    fontSize: '0.875rem',
    '&:hover': {
      backgroundColor: '#4a63e8',
      color: '#FFFFFF'
    }
  }
}))

export const MobileContentCard = ({ item, onNavigate }) => {
  const classes = useStyles()
  const navigate = useNavigate()

  const handleReadMore = () => {
    navigate((`/${item?.sk}/${item?.staticUrl}`))
    onNavigate()
  }

  // The thumbnail is optional: `image` is absent on records written outside the
  // editor, and the editor itself saves an empty `imgUrl` when none is chosen.
  // Both fall back to the same placeholder the other content cards use.
  // 16:9 around the editor's focal point; the box is 140-180px tall.
  const contentImage = getContentImageUrl(item?.image, { ...THUMBNAIL_SIZES.CARD, fallback: MtLogo })

  return (
    <Card className={classes.mobileContentCard} elevation={0}>
      <CardMedia
        component='img'
        image={contentImage}
        alt={item?.title}
        className={classes.mobileContentImage}
      />

      <CardContent className={classes.mobileContentCardContent}>
        <Typography
          variant='h6'
          component='h3'
          className={classes.mobileContentTitle}
          gutterBottom
        >
          {item.title}
        </Typography>

        {(item.metaDescription) && (
          <Typography variant='body2' className={classes.mobileContentDescription} paragraph>
            {item.metaDescription}
          </Typography>
        )}

        <Box className={classes.mobileContentActions}>
          <Button
            variant='contained'
            size='small'
            fullWidth
            onClick={handleReadMore}
            className={classes.mobileReadMoreButton}
            data-testid={`mobile-read-more-${item.sk || item.locationId}`}
          >
            Read More
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}
